import React, { useRef } from 'react';

const TOUCH_CLICK_SUPPRESSION_MS = 750;

// Module-scoped: the delayed compatibility click can arrive after `onPress`
// re-rendered the pressed button away, landing on whichever button now sits
// at the same coordinates (e.g. the first answer card of the next survey
// question). A per-instance ref unmounts with the pressed button, so the
// suppression window must be shared across all instances.
let suppressClicksUntil = 0;

// Test-only: the module-level window would otherwise leak between specs.
export const __resetTouchClickSuppressionForTests = () => {
  suppressClicksUntil = 0;
};

type Props = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick' | 'onPointerDown' | 'onPointerUp' | 'onPointerCancel' | 'type'
> & {
  onPress: () => void;
  type?: 'button' | 'submit';
  touchAction?: 'pan-y' | 'none'; // use it where nothing scrolls (e.g. the kiosk keypad).
};

/**
 * A button whose touch press survives finger drift.
 *
 * iPad Safari normally turns a moving touch into a page pan and cancels the
 * click. `touch-pan-y` still allows vertical scrolling (a scroll gesture
 * cancels the press, which is correct for long option lists), while pointer
 * capture keeps a non-scrolling press assigned to this button until the
 * patient lifts their finger, even if they drift outside its visual bounds.
 * Mouse and keyboard activation continue to use the native click path.
 */
export const TolerantTouchButton = React.forwardRef<HTMLButtonElement, Props>(
  ({ onPress, disabled, className, type = 'button', touchAction = 'pan-y', ...props }, ref) => {
    const touchPointerIdRef = useRef<number | null>(null);

    const releasePointer = (button: HTMLButtonElement, pointerId: number) => {
      if (button.hasPointerCapture(pointerId)) {
        button.releasePointerCapture(pointerId);
      }
    };

    return (
      <button
        ref={ref}
        {...props}
        type={type}
        disabled={disabled}
        className={`${touchAction === 'none' ? 'touch-none' : 'touch-pan-y'} select-none ${className ?? ''}`}
        onPointerDown={event => {
          if (disabled || event.pointerType !== 'touch' || !event.isPrimary) return;

          touchPointerIdRef.current = event.pointerId;
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerUp={event => {
          if (event.pointerId !== touchPointerIdRef.current) return;

          touchPointerIdRef.current = null;
          releasePointer(event.currentTarget, event.pointerId);
          suppressClicksUntil = Date.now() + TOUCH_CLICK_SUPPRESSION_MS;
          onPress();

          if (type === 'submit') {
            event.currentTarget.form?.requestSubmit(event.currentTarget);
          }
        }}
        onPointerCancel={event => {
          if (event.pointerId !== touchPointerIdRef.current) return;

          touchPointerIdRef.current = null;
          releasePointer(event.currentTarget, event.pointerId);
        }}
        onClick={event => {
          // Older iPads may wait hundreds of milliseconds after pointerup
          // before emitting the synthetic click. `detail === 0` identifies
          // keyboard/programmatic activation, which must remain immediate.
          // Leave the window armed until it expires: two rapid taps can leave
          // two compatibility clicks pending, and consuming the window on the
          // first would let the second re-fire a press that pointerup already
          // delivered.
          if (event.detail > 0 && Date.now() <= suppressClicksUntil) {
            event.preventDefault();

            return;
          }

          onPress();
        }}
      />
    );
  }
);

TolerantTouchButton.displayName = 'TolerantTouchButton';
